import {RefObject, useCallback} from 'react';
import Flatpickr from 'react-flatpickr';
import type {FlatpickrOptions} from './BaseDatePicker';

export type UseFlatpickrFunctionsProps = {
    fpRef: RefObject<Flatpickr['flatpickr']>;
    dateFormat: string;
};
export type FlatpickrFunctions = {
    parseDate: (value: string) => ReturnType<Flatpickr['flatpickr']['parseDate']>;
    formatDate: (value: Date) => ReturnType<Flatpickr['flatpickr']['formatDate']>;
    setDate: (value: FlatpickrOptions['defaultDate'], triggerChange?: boolean) => ReturnType<Flatpickr['flatpickr']['setDate']>;
    clearDate: (triggerChange?: boolean) => void;
    isCalendarElement: (el?: Element | null) => boolean;
};
export function useFlatpickr(props: UseFlatpickrFunctionsProps): FlatpickrFunctions {
    const {
        fpRef,
        dateFormat
    } = props;

    const parseDate: FlatpickrFunctions['parseDate'] = useCallback(
        (date) => fpRef.current?.parseDate(date, dateFormat),
        [dateFormat, fpRef]
    );
    const formatDate: FlatpickrFunctions['formatDate'] = useCallback(
        (date) => fpRef.current?.formatDate(date, dateFormat) || '',
        [dateFormat, fpRef]
    );
    const setDate: FlatpickrFunctions['setDate'] = useCallback(
        (date, triggerChange) => date && fpRef.current?.setDate(date, triggerChange, dateFormat),
        [dateFormat, fpRef]
    );
    const clearDate: FlatpickrFunctions['clearDate'] = useCallback(
        (triggerChange?: boolean) => fpRef.current?.clear(triggerChange),
        [fpRef]
    );

    const isCalendarElement = (el?: Element | Document | null): boolean => {
        if (!el || !('closest' in el)) {
            return false;
        }

        if (el.closest('.flatpickr-calendar')) {
            return true;
        }
        return false;
    };

    return {
        parseDate,
        formatDate,
        setDate,
        clearDate,
        isCalendarElement
    };
}
